import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useNetworkState } from '../hooks/useNetworkState';
import { useRedirect } from '../hooks/useRedirect';

const ErrorContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  font-size: 18px;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #f5f5f5;
  font-size: 16px;
`;

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 500px;
  padding: 40px 20px;
  border-radius: 4px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  box-shadow: rgba(0, 0, 0, 0%) 0 0 0 0, rgba(0, 0, 0, 0%) 0 0 0 0,
    rgba(0, 0, 0, 10%) 0 4px 6px -1px, rgba(0, 0, 0, 10%) 0 2px 4px -2px;
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  line-height: 1.2;
  margin: 0;
  margin-bottom: 14px;
`;

const ErrorParagraph = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
  margin-bottom: 7px;
  max-width: 80%;
`;

const NetworkStatus = styled.div`
  font-size: 0.925rem;
  line-height: 1.5;
  margin: 0;
  margin-top: 7px;
  margin-bottom: 7px;
  font-weight: 500;

  div {
    margin-top: 7px;

    width: 100%;

    display: flex;
    justify-content: center;
    align-items: center;
    line-height: none;
  }

  i.status {
    display: inline-block;
    height: 8px;
    width: 8px;
    border-radius: 100%;
    margin: 0 4px;
  }

  div.online {
    color: #4caf50;
  }

  div.online i.status {
    background-color: #4caf50;
  }

  div.offline {
    color: #f44336;
  }

  div.offline i.status {
    background-color: #f44336;
  }
`;

const StyledLink = styled(Link)`
  background: var(--dark-background-color, #6048f3);
  color: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;

  margin-top: 14px;

  transition: all 300ms;

  &:hover {
    opacity: 0.8;
  }
`;

export const Offline = () => {
  const { search } = useLocation();
  const { isOnline } = useNetworkState();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const handleRedirect = useRedirect();
  const [lastChecked, setLastChecked] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setLastChecked(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (isOnline && returnTo) {
      handleRedirect(returnTo);
    }
  }, [returnTo, handleRedirect, isOnline]);

  return (
    <ErrorContainer>
      <ErrorCard>
        <ErrorTitle>Uh Oh!</ErrorTitle>

        <ErrorParagraph>It appears as though you are offline.</ErrorParagraph>

        <ErrorParagraph>Unfortunately we&apos;re unable to load this experience.</ErrorParagraph>

        <ErrorParagraph>
          We will be here as soon as you reconnect, and hope to see you again real soon.
        </ErrorParagraph>

        <NetworkStatus>
          <span>Network Status as of {lastChecked.toLocaleString()}</span>

          <div className={isOnline ? 'online' : 'offline'}>
            <i className="status" />
            <span>{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        </NetworkStatus>

        {returnTo && <StyledLink to="/">Let&apos;s try that again</StyledLink>}
      </ErrorCard>
    </ErrorContainer>
  );
};
